import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Menu, X, CalendarCheck } from "lucide-react";
import Logo, { INSTAGRAM_URL } from "@/components/Logo";

const LINKS = [
  ["Trattamenti", "#trattamenti"],
  ["Approccio", "#approccio"],
  ["Chi sono", "#chisono"],
  ["Recensioni", "#recensioni"],
  ["FAQ", "#faq"],
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "backdrop-blur-xl bg-ink/85 border-b border-gold/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              data-testid={`nav-link-${href.slice(1)}`}
              className="text-sm text-silver hover:text-ivory transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram-link"
            className="flex items-center gap-2 text-sm border border-white/12 rounded-full px-4 py-2 text-silver hover:text-ivory hover:border-gold/50 transition-all"
          >
            <Instagram size={16} /> Seguimi
          </a>
          <a
            href="#prenota"
            data-testid="header-book-button"
            className="flex items-center gap-2 text-sm font-semibold bg-gold text-ink rounded-full px-5 py-2.5 hover:bg-goldlight transition-colors"
          >
            <CalendarCheck size={16} /> Prenota la visita
          </a>
        </div>
        <button
          className="lg:hidden text-ivory p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-gold/15 bg-ink/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map(([label, href]) => (
                <a
                  key={href}
                  href={href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${href.slice(1)}`}
                  className="text-base text-silver hover:text-ivory transition-colors"
                >
                  {label}
                </a>
              ))}
              <a
                href="#prenota"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-book-button"
                className="mt-2 flex items-center justify-center gap-2 text-sm font-semibold bg-gold text-ink rounded-full px-5 py-3"
              >
                <CalendarCheck size={16} /> Prenota la visita
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-instagram-link"
                className="flex items-center justify-center gap-2 text-sm border border-white/12 rounded-full px-5 py-3 text-silver"
              >
                <Instagram size={16} /> Seguimi su Instagram
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
